import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { ApplicationSettingsType } from '@fi-shared/types/application-settings-type';
import { AccessRightType } from '@fi-shared/types/access-right-type';
import { ForumService } from '../../../forum/forum.service';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { NewsFeedService } from '../../../news-feed/news-feed.service';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { Subject } from 'rxjs';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { WidgetPlaceHolderControlModel } from '@fi-shared/widget/widget-place-holder-control.model';
import { takeUntil } from 'rxjs/operators';
import { WidgetSharedService } from '@fi-shared/widget/widget-shared.service';
import { FiBackendRequestType, FiRequestsQueueService } from '@fi-shared/fi-shared-services/fi-requests-queue.service';
import { FiAppSettings } from '@fi-shared/fi-app-settings';
import {
    ForumListingSettingsDescriptionLimit,
    ForumListingSettingsDescriptionLimitIds,
} from '@fi-app/widget-settings/controls/forum-listing/description-limit-options';
import { isDefined } from '@fi-shared/utils';
import { truncateAtWord } from '@fi-shared/utils/truncate-at-word';

@Component({
    selector: 'fi-widget-content-forum-listing',
    template: `
        <div class="ForumListingContainer">
          @for (forum of forumData$ | async; track forum) {
            <div class="widget-list-item-item">
              <span class="widget-list-item-icon fa fa-comment-o"></span>
              <div class="widget-list-item-content">
                <div class="widget-list-item-header">
                  <a
                    class="widget-list-item-link BrandedColor"
                    [fiStateSref]="FiStateNames.Forum.View"
                    [fiStateSrefParams]="{ id: forum.Id }"
                    [attr.title]="forum.Name"
                    >{{ forum.Name }}</a
                    >
                    <span class="count-wrap">
                      @if (control.Settings.ShowNewForumPostsCount && forum.NewForumPostsCount > 0) {
                        <a
                          class="count BrandedBackgroundColor"
                          [fiStateSref]="FiStateNames.Forum.View"
                          [fiStateSrefParams]="{ id: forum.Id, isOnlyUnvisited: true }"
                          >{{ forum.NewForumPostsCount }}</a
                          >
                        }
                      </span>
                    </div>
                    <div class="clearfix"></div>
                    @if (control.Settings.ShowDescription) {
                      <div class="widget-list-item-summary">
                        {{ getForumDescription(forum.Description) }}
                      </div>
                    }
                  </div>
                </div>
              }
            </div>
        `,
    styleUrls: ['../../../../../Source/app/shared/widget/ForumListing.scss', './forum-listing.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-widget-content-forum-listing' },
    standalone: false,
})
export class WidgetContentForumListingComponent implements OnInit, OnDestroy {
    @Input() control: WidgetPlaceHolderControlModel;

    forumData$ = new BehaviorSubject<any[]>([]);
    FiStateNames = FiStateNames;

    private _isEmpty$ = new BehaviorSubject<boolean>(true);
    get isEmpty$() {
        return this._isEmpty$.asObservable();
    }

    constructor(
        private cdr: ChangeDetectorRef,
        private newsFeedService: NewsFeedService,
        private forumService: ForumService,
        private widgetSharedService: WidgetSharedService,
        private fiRequestsQueueService: FiRequestsQueueService,
        private accessToApplicationTypeService: AccessToApplicationTypeService,
    ) {}

    ngOnInit() {
        this.widgetSharedService
            .getWidgetSettingsChanged$(() => this.control.Settings)
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => {
                this.cdr.markForCheck();
            });

        const hasAccessToForum = this.accessToApplicationTypeService.HasAccessToApplicationType(
            ApplicationSettingsType.Forum,
            AccessRightType.Share,
        );

        if (hasAccessToForum) {
            setTimeout(() => {
                this.loadData();
            }, FiAppSettings.StartDelay);
            this.newsFeedService.newNewsFeedsChanged$.takeUntil(this.ngOnDestroy$).subscribe((data) => {
                if (data.forumId && data.forumId !== -1) {
                    const dataItems = this.forumData$.value.filter((item) => item.Id == data.forumId);
                    if (dataItems && dataItems.length && data.delta) {
                        dataItems[0].NewForumPostsCount += data.delta;
                        this.cdr.detectChanges();
                    }
                }
            });
        }
    }

    getForumDescription(forumDescription: string = ''): string {
        if (
            !isDefined(this.control.Settings.DescriptionLimit) ||
            this.isForumDescriptionReductionType(ForumListingSettingsDescriptionLimitIds.Medium)
        ) {
            return truncateAtWord(forumDescription, ForumListingSettingsDescriptionLimit.Medium);
        }

        if (this.isForumDescriptionReductionType(ForumListingSettingsDescriptionLimitIds.Small)) {
            return truncateAtWord(forumDescription, ForumListingSettingsDescriptionLimit.Small);
        }

        if (this.isForumDescriptionReductionType(ForumListingSettingsDescriptionLimitIds.Big)) {
            return truncateAtWord(forumDescription, ForumListingSettingsDescriptionLimit.Big);
        }

        return forumDescription;
    }

    private isForumDescriptionReductionType(limitId: ForumListingSettingsDescriptionLimitIds) {
        return this.control.Settings.DescriptionLimit === limitId;
    }

    private loadData() {
        this.fiRequestsQueueService.addRequestToQueue(() => {
            const promise = this.forumService.getForumsForWidget(true).then((data) => {
                this.forumData$.next(data);
                this._isEmpty$.next(!data.length);
            });
            return promise;
        }, FiBackendRequestType.WidgetForumListing);
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
